/** Operator-facing labels — read-only payment receipt + evidence links; no payment/settlement actions. */

import type { CollectionCaseEvidenceLinkKind } from '@shared/collection/collection-case-read-links-types';
import {
  HARDCODED_DOCUMENT_TYPE_LABELS,
  HARDCODED_EVIDENCE_TYPE_LABELS,
} from '@shared/content/operational-labels/operational-label-defaults';

export const COLLECTION_CASE_READ_LINKS_OPERATOR_LABELS = {
  sectionTitle: '수납·증빙 연결',
  sectionSubtitle: '조회 전용 · 별도 payment flow',
  loading: '연결 정보를 불러오는 중…',
  loadFailed: '연결 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
  retryAction: '다시 불러오기',
  emptyReceipt: '등록된 수납 영수증 없음 · 수납은 별도 payment flow에서 처리됩니다',
  emptyEvidence: '등록된 연락 증빙 없음',
  subsectionPaymentReceipt: '수납 영수증',
  subsectionEvidence: '연락 증빙',
  viewDetail: '조회',
  footer: '실시간 조회 · 자동 조치 없음 · 수납은 별도 payment flow에서 처리됩니다',
  paymentReceiptCash: '수납 영수증 (현금)',
  paymentReceiptDocumentPresentSuffix: ' · Posting 확인 중',
  paymentReceiptInconsistentPrefix: '수납 상태 확인 필요',
} as const;

const EVIDENCE_KIND_LABELS: Record<CollectionCaseEvidenceLinkKind, string> = {
  collection_contact_note:
    HARDCODED_DOCUMENT_TYPE_LABELS.collection_contact_note ?? '채권 회수 연락 기록',
  collection_contact_proof:
    HARDCODED_EVIDENCE_TYPE_LABELS.collection_contact_proof ?? '채권 회수 연락 증빙',
};

const PAYMENT_RECEIPT_REASON_HINTS: Partial<Record<string, string>> = {
  PAYMENT_RECEIPT_DOCUMENT_MISSING: '수금전표 문서 누락',
  PAYMENT_RECEIPT_WITHOUT_POSTING_RECORD: '수금전표 회계전표 미생성',
  PAYMENT_RECEIPT_POSTING_RECORD_FAILED: '수금전표 회계전표 생성 오류',
  PAYMENT_RECEIPT_POSTING_RECORD_PENDING: '수금전표 회계전표 생성 대기',
  AR_SETTLEMENT_INCOMPLETE: '수금 반영 미완료',
  AR_SETTLEMENT_PAYMENT_POSTING_MISMATCH: '수금 전표 ID 불일치',
  AR_MARKED_PAID_WITHOUT_RECEIPT_DOCUMENT: '완납 표시 · 수금전표 참조 누락',
  AR_MARKED_PAID_WITHOUT_PAYMENT_POSTING: '완납 표시 · 수금 회계전표 참조 누락',
  RECEIPT_DOCUMENT_WITHOUT_AR_ENTRY: '수금전표 · 채권 원장 미연결',
  PAYMENT_RECEIPT_AMOUNT_MISMATCH: '수금 금액/계정 불일치',
  PAID_AR_OUTSTANDING_BALANCE_NONZERO: '완납 표시 · 미수잔액 잔존',
  PAID_AR_PAID_AMOUNT_NOT_INVOICE: '완납 표시 · 납부액≠청구액',
  DUPLICATE_PAYMENT_RECEIPT_ARTIFACT: '중복 수금전표',
  CASH_SALE_WITH_PAYMENT_RECEIPT: '현금매출에 수금전표 생성됨',
  UNSUPPORTED_PAYMENT_RECEIPT_PAYMENT_MODE: '미지원 수금 방식',
};

export function labelForCollectionCaseEvidenceLinkKind(
  kind: CollectionCaseEvidenceLinkKind
): string {
  return EVIDENCE_KIND_LABELS[kind];
}

export function labelForCollectionCasePaymentReceiptLink(input: {
  paymentMode: 'cash' | null;
  status: 'document_present' | 'posted' | 'inconsistent';
}): string {
  const base =
    input.paymentMode === 'cash'
      ? COLLECTION_CASE_READ_LINKS_OPERATOR_LABELS.paymentReceiptCash
      : COLLECTION_CASE_READ_LINKS_OPERATOR_LABELS.paymentReceiptCash;
  if (input.status === 'document_present') {
    return `${base}${COLLECTION_CASE_READ_LINKS_OPERATOR_LABELS.paymentReceiptDocumentPresentSuffix}`;
  }
  return base;
}

export function consistencyHintForPaymentReceiptReasonCode(reasonCode: string): string {
  return (
    PAYMENT_RECEIPT_REASON_HINTS[reasonCode] ??
    `${COLLECTION_CASE_READ_LINKS_OPERATOR_LABELS.paymentReceiptInconsistentPrefix} (${reasonCode})`
  );
}

export function buildCollectionCaseReadLinksBoundaryCopy(): {
  footer: string;
  emptyReceipt: string;
} {
  return {
    footer: COLLECTION_CASE_READ_LINKS_OPERATOR_LABELS.footer,
    emptyReceipt: COLLECTION_CASE_READ_LINKS_OPERATOR_LABELS.emptyReceipt,
  };
}
